import { forwardRef } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQueryParams } from '@/contexts/QueryParamContext'
import { buildInternalHref } from '@/utils/urlUtils'

// Protocols that never receive marketing params, even when `external` is set.
const NON_FORWARDING_PROTOCOL_RE = /^(mailto:|tel:|javascript:)/i
const ABSOLUTE_URL_RE = /^https?:\/\//i

const PreservedLink = forwardRef(
  (
    {
      to,
      children,
      newParams = {},
      external = false,
      onClick,
      replace,
      ...props
    },
    ref
  ) => {
    const [searchParams] = useSearchParams()
    const { appendQueryParams } = useQueryParams()

    if (NON_FORWARDING_PROTOCOL_RE.test(to) && !external) {
      return (
        <a ref={ref} href={to} onClick={onClick} {...props}>
          {children}
        </a>
      )
    }

    const isExternal = external || ABSOLUTE_URL_RE.test(to)

    if (isExternal) {
      let href = appendQueryParams(to)
      if (Object.keys(newParams).length > 0) {
        try {
          const urlObj = new URL(href)
          Object.entries(newParams).forEach(([key, value]) => {
            urlObj.searchParams.set(key, value)
          })
          href = urlObj.toString()
        } catch {
          // href stays as the appendQueryParams result if not a valid absolute URL
        }
      }
      return (
        <a ref={ref} href={href} onClick={onClick} {...props}>
          {children}
        </a>
      )
    }

    const toWithParams = buildInternalHref(to, searchParams, newParams)

    return (
      <Link
        ref={ref}
        to={toWithParams}
        replace={replace}
        onClick={onClick}
        {...props}
      >
        {children}
      </Link>
    )
  }
)

PreservedLink.displayName = 'PreservedLink'

export default PreservedLink
